import { AgentAppActions } from '../../agent/services/agent-app-actions.service';
import { AgentApplicationHost } from '../../agent/interfaces/app-action';
import { workspaceSource } from '../../agent/workspace-metadata';
import { AgentWorkspaceRegistry } from '../../agent/services/agent-workspace-registry.service';
import { WorkspaceViewSaveService } from '../../services/workspace-view-save.service';
import { ChangeDetectorRef, Component, EventEmitter, inject, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { DataViewerComponent } from '../../components/tab-models/data-viewer/data-viewer.component';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatTabsModule} from '@angular/material/tabs';

import { PropiertiesOverviewComponent } from '../../components/propierties-overview/propierties-overview.component';
import { ViewsOverviewComponent } from '../../components/views-overview/views-overview.component';
import { INeo4j } from '../../interfaces/Neo4j';
import { ACCOUNT, APP_TITLE, CHANGE_DATABASE_lower, LOGOUT, MODELS, NEW_TAB, PROPERTIES, SCHEMAS, TAXONOMY_EXPLORER, USER_VIEWS, VIEWS, VIEWS_MENU } from '../../shared/strings';
import { SharedService } from '../../services/shared.service';
import { Router } from '@angular/router';

import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { TaxonomyOverviewComponent } from '../../components/taxonomy-overview/taxonomy-overview.component';
import { Importer } from '../../clases/Importer';
import { ImportEnum } from '../../enums/Import';
import { Exporter } from '../../clases/Exporter';
import { DataService } from '../../interfaces/DataService';
import { DataViewer } from '../../abstract/DataViewer';
import { ViewVisualizerComponent } from '../../components/tab-models/view-visualizer/view-visualizer.component';
import { Neo4jCreatorComponent } from '../../components/tab-models/neo4j-creator/neo4j-creator.component';
import { SchemaTree } from '../../clases/SchemaTree';
import { MatIconModule } from '@angular/material/icon';
import { generateUUID } from '../../utils/utils';
import { Overlay } from '@angular/cdk/overlay';
import { ITab } from '../../interfaces/ITab';
import {MatTooltipModule} from '@angular/material/tooltip';
import { LeftSidebarMenuComponent } from '../../components/left-sidebar-menu/left-sidebar-menu.component';
import { ISideBarButtons } from '../../interfaces/ISidebarMenu';
import { MatDialog } from '@angular/material/dialog';
import { ChangeDatabaseDialogComponent } from '../../components/dialogs/change-database-dialog/change-database-dialog.component';
import { UserDialogComponent } from '../../components/dialogs/user-dialog/user-dialog.component';
import { BeNeo4jService } from '../../services/be-neo4j.service';
import { INeo4jData } from '../../interfaces/INeo4jData';
import { TabType } from '../../enums/TabType';
import { RightSidebarMenuComponent } from '../../components/right-sidebar-menu/right-sidebar-menu.component';
import { ModelsOverviewComponent } from '../../components/models-overview/models-overview.component';
import { SchemaOverviewComponent } from '../../components/schema-overview/schema-overview.component';
import { TaskNotificationBarComponent } from '../../components/task-notification-bar/task-notification-bar.component';
import { SimulationComponent } from '../../components/tab-models/simulation/simulation.component';
import { firstValueFrom, Observable } from 'rxjs';
import { Model } from '../../interfaces/Model';

@Component({
  selector: 'app-main',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatButtonModule,
    MatToolbarModule,
    MatTabsModule,
    PropiertiesOverviewComponent,
    ViewsOverviewComponent,
    TaxonomyOverviewComponent,
    DataViewerComponent,
    ViewVisualizerComponent,
    SimulationComponent,
    MatMenuModule,
    Neo4jCreatorComponent,
    MatIconModule,
    MatTooltipModule,
    LeftSidebarMenuComponent,
    RightSidebarMenuComponent,
    ModelsOverviewComponent,
    SchemaOverviewComponent
  ],
  templateUrl: './main.component.html',
  styleUrl: './main.component.scss'
})
export class MainComponent {
  private readonly agentWorkspaces = inject(AgentWorkspaceRegistry);
  private readonly agentActions = inject(AgentAppActions);
  private readonly workspaceSave = inject(WorkspaceViewSaveService);
  private readonly agentHost: AgentApplicationHost = {
    openTab: () => this.newTabFromStorage(),
    loadModel: (modelName, destination) => this.loadAgentModel(modelName, destination),
    restoreView: (viewName, destination) => this.viewsOverview.restoreAgentView(viewName, destination),
    saveViewAs: async viewName => {
      const owner = this.getCurrentDataView();
      if (!owner) throw new Error('No active View to save');
      await this.workspaceSave.save(owner, { name: viewName, saveAs: true,
        isCurrent: () => this.components.toArray().includes(owner) });
      this.viewsOverview.getViews();
    },
    setTabMode: (id, tabType) => this.setCurrentTabMode(id, tabType),
    applySimulationConfiguration: (id, modelUuid, revision, configuration, openConfiguration) => this.applyAgentSimulationConfiguration(id, modelUuid, revision, configuration, openConfiguration),
    runSimulation: (id, revision) => this.runAgentSimulation(id, revision),
    saveSimulation: (id, mode, name) => this.saveAgentSimulation(id, mode, name),
    loadSimulation: (id, simulationUuid) => this.loadAgentSimulation(id, simulationUuid),
    activateTab: id => this.changeTab(this.tabs.findIndex(tab => tab.id === id)),
    closeTab: id => this.closeTab(this.tabs.findIndex(tab => tab.id === id)),
    openPanel: panel => {
      const panels = { models: this.modelOverview, views: this.viewsOverview, schemas: this.schemaOverview,
        taxonomy: this.taxonomyOverview, properties: this.propiertiesOverview };
      if (panels[panel].isOverviewActive) return;
      const open = { models: this.onModelsOverview, views: this.onViewsOverview, schemas: this.onSchemasOverview,
        taxonomy: this.onTaxonomyOverview, properties: this.onPropiertiesOverview };
      open[panel]();
    }
  };

  ngDoCheck(): void {
    this.agentActions.attach(this.agentHost);
    const components = this.components?.toArray() ?? [];
    this.agentWorkspaces.sync(this, this.tabs.map(tab => {
      const component = components.find(candidate => candidate.agentTabId === tab.id);
      return workspaceSource(tab, component ?? null, this.pendingImportByTabId.get(tab.id)?.connection);
    }), this.tabs[this.selectedIndex]?.id, () => this.ngDoCheck());
  }

  ngOnDestroy(): void {
    this.agentActions.detach(this.agentHost);
    this.agentWorkspaces.detach(this);
  }

  @ViewChildren(DataViewer) components!: QueryList<DataViewer>;
  @ViewChild(PropiertiesOverviewComponent) propiertiesOverview!: PropiertiesOverviewComponent;
  @ViewChild(ViewsOverviewComponent) viewsOverview!: ViewsOverviewComponent;
  @ViewChild(TaxonomyOverviewComponent) taxonomyOverview!: TaxonomyOverviewComponent;
  @ViewChild(SchemaOverviewComponent) schemaOverview!: SchemaOverviewComponent;
  @ViewChild(ModelsOverviewComponent) modelOverview!: ModelsOverviewComponent;
  @ViewChild('menuTrigger') menuTrigger!: MatMenuTrigger;
  @ViewChild('rightMenu') rightMenu!: RightSidebarMenuComponent;

  importData: EventEmitter<Importer> = new EventEmitter<Importer>();

  constructor(
    private beNeo4jService: BeNeo4jService,
    private changeDetector: ChangeDetectorRef,
    private sharedService: SharedService,
    private router: Router,
    private overlay: Overlay
  ){}
  
  //String definitions
  APP_TITLE = APP_TITLE;
  PROPIERTIES = PROPERTIES;
  VIEWS = VIEWS;
  NEW_TAB = NEW_TAB;
  LOGOUT = LOGOUT;
  TAXONOMY_EXPLORER = TAXONOMY_EXPLORER;
  VIEWS_MENU = VIEWS_MENU;
  USER_VIEWS = USER_VIEWS;
  CHANGE_DATABASE_lower = CHANGE_DATABASE_lower;
  ACCOUNT = ACCOUNT;
  SCHEMAS = SCHEMAS;
  MODELS = MODELS;
  
  protected readonly TabType = TabType;

  dialog = inject(MatDialog);
  
  tabs: ITab[] = [];
  selectedIndex: number = 0;
  contextIndex: number = 0;

  leftSideButtonsElements: {
    topButtons: ISideBarButtons[], 
    bottomButtons:ISideBarButtons[]
  } = {
    topButtons: [],
    bottomButtons: []
  }

  rightSideButtonsElements: {
    topButtons: ISideBarButtons[], 
    bottomButtons:ISideBarButtons[]
  } = {
    topButtons: [],
    bottomButtons: []
  }

  enableTaxonomyButton: boolean = true;

  private accountDialogOpenCount = 0;
  private changeDatabaseDialogOpenCount = 0;

  private pendingImportByTabId: Map<string, Importer> = new Map();

  ngAfterViewInit() {
    if (this.components.get(0)) {
      let component = this.components.get(0)!;
      let sub = component.getDataReadyFocus().subscribe(() => {
        this.propiertiesOverview.assignOverviewParameters(this.components.get(0));
        sub.unsubscribe();
      });
    }
    const tabsContainer = document.getElementById('tabs-container');
    if (tabsContainer) {
      tabsContainer.addEventListener('wheel', (event) => {
        if (event.deltaY > 0) {
          tabsContainer.scrollLeft += 100;
        } else {
          tabsContainer.scrollLeft -= 100;
        }
      });
      this.selectedIndex = 0;
    }

    this.importData.subscribe(this.onImportAction);

    this.newTabFromStorage();

    this.createLeftSidebarMenu();

    this.rightSideButtonsElements = {
      topButtons: [
        {
          text: "Properties",
          icon: "assets/sidebar_icons/properties.svg",
          callback: this.onPropiertiesOverview,
          isActive: () => this.propiertiesOverview.isOverviewActive,
          disabled: false
        }
      ],
      bottomButtons: [
        {
          text: "Logout",
          icon: "assets/sidebar_icons/logout.svg",
          callback: this.onLogout,
          disabled: false
        }
      ]
    }
  }

  onObtainCurrentExport(): Observable<Exporter>{
    return this.getCurrentDataView()!.onExportData();
  }

  onImportAction = (importer: Importer)=> {
    if (importer.type === ImportEnum.IMPORT_NEW_TAB || importer.type === ImportEnum.MODEL_NEW_TAB) {
      this.onNewTab(importer);
      return;
    }
    let dataView = this.getCurrentDataView();
    if (dataView) dataView.inyectConnectionData(importer);
  }

  /** Resolve a canonical Model through the same permission-filtered catalog and
   * load path used by the Models UI. A named load is one atomic Agent action:
   * never leave behind a blank tab when lookup or Cypher retrieval fails. */
  private async loadAgentModel(modelName: string, destination: 'NEW_TAB' | 'CURRENT_CREATOR'): Promise<void> {
    const originalTabId = this.tabs[this.selectedIndex]?.id;
    const requested = modelName.trim().toLocaleLowerCase();
    const matches: { model: Model; stringConnection: string }[] = [];
    let page = 1;
    let more = true;
    while (more && page <= 100) {
      // An empty search avoids interpolating a user-supplied name into the
      // legacy catalog's Cypher search. Match the displayed name locally.
      const response = await firstValueFrom(this.beNeo4jService.getSchemas(page, 100, ''));
      if (response.status !== 200) throw new Error('Model catalog could not be loaded');
      const groups = response.result ?? [];
      for (const group of groups) {
        for (const model of group.models ?? []) {
          if (model.name.trim().toLocaleLowerCase() === requested) {
            matches.push({ model, stringConnection: group.string_connection });
          }
        }
      }
      more = groups.some(group => (group.models?.length ?? 0) === 100);
      page++;
    }
    if (more) throw new Error('Model catalog is too large to resolve this name safely');
    if (!matches.length) throw new Error(`Model “${modelName}” was not found or is not accessible`);
    if (matches.length !== 1) throw new Error(`Model “${modelName}” is ambiguous across Data Services`);
    if (this.tabs[this.selectedIndex]?.id !== originalTabId) throw new Error('Active tab changed while resolving the Model');
    const { model, stringConnection } = matches[0];
    const dataService = this.sharedService.data_services?.find(service => service.string_connection === stringConnection);
    if (!dataService) throw new Error('Model Data Service is unavailable');
    if (destination === 'CURRENT_CREATOR') {
      const current = this.getCurrentDataView();
      if (!(current instanceof Neo4jCreatorComponent)
          || current.savedDocument?.connection?.dataService?.data_service_id !== dataService.data_service_id) {
        throw new Error('The current tab is not a Creator for this Model’s Data Service');
      }
    }
    const result = await firstValueFrom(this.beNeo4jService.getCypherById(model.uuid, model.hierarchy, true, stringConnection));
    const cypher = (result as { result?: unknown })?.result;
    if (typeof cypher !== 'string' || !cypher.trim()) throw new Error('Model query is unavailable');
    if (this.tabs[this.selectedIndex]?.id !== originalTabId) throw new Error('Active tab changed before loading the Model');
    const load = (start: (done: (error?: Error) => void) => void) => new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Timed out while loading the Model Canvas')), 30000);
      start(error => {
        clearTimeout(timeout);
        if (error) reject(error); else resolve();
      });
    });
    if (destination === 'CURRENT_CREATOR') {
      await load(done => this.getCurrentDataView()!.executeFromMenu(cypher, done));
      return;
    }
    const connection: INeo4j = {
      connection: { dataService, DBlabels: [], DBrelations: [], hierarchy: [], schemaTree: [] },
      name_value: model.name, containers: [], model_UUID: model.uuid, hierarchy: model.hierarchy
    };
    const importer = new Importer(connection, ImportEnum.MODEL_NEW_TAB, undefined, cypher);
    const existingTabs = new Set(this.tabs.map(tab => tab.id));
    let newTabId: string | undefined;
    try {
      await load(done => {
        importer.onModelLoaded = done;
        this.onImportAction(importer);
        newTabId = this.tabs.find(tab => !existingTabs.has(tab.id))?.id;
        if (!newTabId) done(new Error('The Model tab could not be created'));
      });
    } catch (error) {
      // A failed backend execution must not strand the blank tab that the
      // normal UI import path created. Preserve it if the user added content.
      const index = this.tabs.findIndex(tab => tab.id === newTabId);
      const component = this.components?.toArray().find(candidate => candidate.agentTabId === newTabId);
      if (index >= 0 && (!component || component.canvasItems.length === 0)) this.closeTab(index);
      throw error;
    }
  }

  /** Restore a saved View through the same UI pathways as its View Library menu. */
  async restoreAgentView(importer: Importer, destination: 'CURRENT_CREATOR' | 'NEW_TAB'): Promise<void> {
    if (destination === 'NEW_TAB') {
      this.onImportAction(importer);
      return;
    }
    const tab = this.tabs[this.selectedIndex];
    if (!tab) return;
    // A restore in place always becomes a Creator. The original persisted View
    // remains untouched until the user explicitly saves it.
    tab.type = TabType.MODEL_CREATOR;
    this.changeDetector.detectChanges();
    await new Promise<void>((resolve, reject) => setTimeout(() => {
      const component = this.getCurrentDataView();
      if (!component) { reject(new Error('Creator is no longer available')); return; }
      component.inyectConnectionData(importer, error => error ? reject(error) : resolve());
      const index = this.components.toArray().findIndex(candidate => candidate === component);
      this.onTabChange(index);
    }));
  }

  createLeftSidebarMenu() {
    this.leftSideButtonsElements = {
      topButtons: [
        {
          text: MODELS,
          icon: "assets/sidebar_icons/models.svg",
          callback: this.onModelsOverview,
          isActive: () => this.modelOverview.isOverviewActive,
          disabled: false
        },
        {
          text: SCHEMAS,
          icon: "assets/sidebar_icons/schema.svg",
          callback: this.onSchemasOverview,
          isActive: () => this.schemaOverview.isOverviewActive,
          disabled: false
        },
        {
          text: USER_VIEWS,
          icon: "assets/sidebar_icons/views.svg",
          callback: this.onViewsOverview,
          isActive: () => this.viewsOverview.isOverviewActive,
          disabled: false
        },
        {
          text: TAXONOMY_EXPLORER,
          icon: "assets/sidebar_icons/taxonomy_explorer.svg",
          callback: this.onTaxonomyOverview,
          isActive: () => this.taxonomyOverview.isOverviewActive,
          disabled: !this.enableTaxonomyButton
        },
        // {
        //   text: "Metrics",
        //   icon: "assets/sidebar_icons/metrics.svg",
        //   callback: ()=>{}
        // }
      ],
      bottomButtons: [
        {
          text: ACCOUNT,
          icon: "assets/sidebar_icons/account.svg",
          disabled: false,
          isActive: () => this.accountDialogOpenCount > 0,
          callback: this.showUserDialog
        },
        {
          text: CHANGE_DATABASE_lower,
          icon: "assets/sidebar_icons/change_database.svg",
          disabled: false,
          isActive: () => this.changeDatabaseDialogOpenCount > 0,
          callback: this.showChangeDatabaseDialog
        }
      ]
    }
  }

  onTabChange(event: number){
    this.propiertiesOverview.unsubscribe();
    setTimeout(()=>{
      this.propiertiesOverview.assignOverviewParameters(this.components.get(event));
    },0);
    this.propiertiesOverview.close();
    let component = this.components.get(event)!;
    this.viewsOverview.setCurrentComponent(component);

    const tab = this.tabs[event];
    if (tab) {
      const pending = this.pendingImportByTabId.get(tab.id);
      if (pending) {
        this.pendingImportByTabId.delete(tab.id);
        this.importProcedure(pending, true, false);
      }
    }

    if(this.components.length === 1) return;
    if(component.getConnectionJson()){
      this.sharedService.data_service = component.getConnectionJson().connection.dataService;
      this.changeSchemaTree(component.getSchemaTree());
      this.viewsOverview.setView(component.getViewFromServer());
    }
    this.showTaxonomyButton(component);
    this.createLeftSidebarMenu();
    if(this.modelOverview.isOverviewActive) this.modelOverview.getModels();
  }

  showTaxonomyButton(currentComponent: DataViewer){
    if(currentComponent instanceof Neo4jCreatorComponent){
      
      this.enableTaxonomyButton = true;
      this.createLeftSidebarMenu();
      return;
    }
    this.enableTaxonomyButton = false;
    this.createLeftSidebarMenu();
  }

  showChangeDatabaseDialog = ()=> {
    this.changeDatabaseDialogOpenCount++;
    const dialogRef = this.dialog.open(ChangeDatabaseDialogComponent, {
      data: {},
      panelClass: 'change-database-dialog'
    });
    dialogRef.afterClosed().subscribe({
      next: (data_service: DataService)=>{
        this.changeDatabaseDialogOpenCount = Math.max(0, this.changeDatabaseDialogOpenCount - 1);
        if(!data_service) return;
        let connection = {
          DBrelations: [],
          DBlabels: [],
          hierarchy: [],
          dataService: data_service,
          schemaTree: [],
        } as INeo4jData;

        let conn = {
          name_value: "",
          containers: [],
          connection: connection,
          model_UUID: "",
          hierarchy: "",
        } as INeo4j;

        let importer = {
          connection: conn,
          type: ImportEnum.NEW_TAB_CONNECTION,
          view: undefined
        } as Importer;

        this.onNewTab(importer);
      }
    });
  }

  showUserDialog = ()=> {
    this.accountDialogOpenCount++;
    const dialogRef = this.dialog.open(UserDialogComponent, {
      data: this.sharedService.user_attr,
      panelClass: 'user-dialog'
    });
    dialogRef.afterClosed().subscribe({
      next: () => {
        this.accountDialogOpenCount = Math.max(0, this.accountDialogOpenCount - 1);
      }
    });
  }

  changeSchemaTree(tree: SchemaTree[]){
    this.taxonomyOverview.setSchemaTree(tree);
  }

  onPropiertiesOverview = ()=>{
    this.propiertiesOverview.toggle();
  }

  onViewsOverview = ()=>{
    if(this.schemaOverview.isOverviewActive) this.schemaOverview.close();
    if(this.taxonomyOverview.isOverviewActive) this.taxonomyOverview.close();
    if(this.modelOverview.isOverviewActive) this.modelOverview.close();
    this.viewsOverview.toggle();
  }

  onModelsOverview = ()=>{
    if(this.schemaOverview.isOverviewActive) this.schemaOverview.close();
    if(this.taxonomyOverview.isOverviewActive) this.taxonomyOverview.close();
    if(this.viewsOverview.isOverviewActive) this.viewsOverview.close();
    this.modelOverview.toggle();
  }

  onTaxonomyOverview = ()=>{
    if(this.schemaOverview.isOverviewActive) this.schemaOverview.close();
    if(this.viewsOverview.isOverviewActive) this.viewsOverview.close();
    if(this.modelOverview.isOverviewActive) this.modelOverview.close();
    this.taxonomyOverview.toggle();
  }

  onSchemasOverview = ()=>{
    if(this.taxonomyOverview.isOverviewActive) this.taxonomyOverview.close();
    if(this.viewsOverview.isOverviewActive) this.viewsOverview.close();
    if(this.modelOverview.isOverviewActive) this.modelOverview.close();
    this.schemaOverview.toggle();
  }

  changeTab(index: number){
    let dataView = this.getCurrentDataView();
    if(dataView){
      this.showTaxonomyButton(dataView);
    }
    this.selectedIndex = index;
    this.changeDetector.detectChanges();
  }

  getCurrentDataView = (): DataViewer | null => {
    if(!this.components) return null;
    return this.components.get(this.selectedIndex)!;
  }

  getLastDataView = (): DataViewer | undefined =>{
    let component = this.components.get(this.components.length - 1);
    return component;
  }

  onNewTab = (importer: Importer) => {
    this.sharedService.data_service = undefined;
    if(!this.sharedService.data_services) return;
    let dataservice = this.sharedService.data_services.find((service: DataService)=>service.data_service_id === importer.connection.connection.dataService.data_service_id);
    this.sharedService.data_service = dataservice;
    let dsData = this.sharedService.data_service_data;
    let connData = importer.connection.connection;
    if(dsData?.dataService?.data_service_id === dataservice?.data_service_id){
      connData.DBlabels = dsData.DBlabels;
      connData.DBrelations = dsData.DBrelations;
      connData.hierarchy = dsData.hierarchy;
      connData.schemaTree = dsData.schemaTree;
    }

    const tabId = generateUUID();
    let tabName = (importer.connection.name_value) ? importer.connection.name_value : 'Tab '+(this.tabs.length+1);
    this.tabs.push({id: tabId, label: tabName, dataservice: dataservice!.data_service_name, type: this.selectTabType(importer.type)});
    this.taxonomyOverview.close();

    this.pendingImportByTabId.set(tabId, importer);
    this.changeTab(this.tabs.length - 1);
  }

  importProcedure = (importer: Importer, currentComponent: boolean = false, changeTab: boolean = true) => {
    let component = currentComponent ? this.getCurrentDataView() : this.getLastDataView();
    if (
      importer.type === ImportEnum.IMPORT_IN_VIEW ||
      importer.type === ImportEnum.IMPORT_NEW_TAB
    ) {
      if (!component) return;
      component!.inyectConnectionData(importer);
      let index = this.components.toArray().findIndex((cp: DataViewer) => cp == component);
      this.onTabChange(index);
      
    } else if (importer.type === ImportEnum.MODEL_NEW_TAB) {
      if (!component) return;
      component!.onNewView(importer, () => { component!.executeFromMenu(importer.cypher!, importer.onModelLoaded) });
    } else {
      if (component) {
        component.onNewView(importer);
      }
    }

    // if (changeTab) {
    //   this.changeTab(this.tabs.length - 1);
    // } else {
    //   this.propiertiesOverview.assignOverviewParameters(component!);
    //   this.showTaxonomyButton(component!);
    //   this.createLeftSidebarMenu();
    // }

    this.propiertiesOverview.assignOverviewParameters(component!);
      this.showTaxonomyButton(component!);
      this.createLeftSidebarMenu();
  }

  changeTabType = (tabType: TabType) =>{
    let component = this.getCurrentDataView();
    if (!component) return;

    let cameraState = component.getCameraState();
    let currentTab = this.tabs[this.selectedIndex];
    if (!currentTab) return;

    component.onExportData().subscribe({
      next: (exporter: Exporter) => {
        let document = {
          ...exporter.connection,
          connection: {
            ...component.savedDocument.connection,
            dataService: exporter.connection.connection.dataService
          }
        } as INeo4j;
        let importer = {
          connection: document,
          type: ImportEnum.IMPORT_IN_VIEW,
          view: exporter.view || component.getViewFromServer()
        } as Importer;
        currentTab.type = tabType;
        this.changeDetector.detectChanges();

        setTimeout(()=>{
          this.importProcedure(importer, true, false);
          this.getCurrentDataView()?.preserveCameraState(cameraState);
        }, 0)
      }
    });
  }

  /** Shared transition boundary for the mode buttons and typed Agent actions. */
  setCurrentTabMode(tabId: string, tabType: TabType): void {
    const currentTab = this.tabs[this.selectedIndex];
    if (!currentTab || currentTab.id !== tabId) throw new Error('Target tab is no longer active');
    if (currentTab.type === tabType) return;
    this.changeTabType(tabType);
  }

  private async simulationForAgent(tabId: string): Promise<SimulationComponent> {
    if (this.tabs[this.selectedIndex]?.id !== tabId || this.tabs[this.selectedIndex]?.type !== TabType.SIMULATION) {
      throw new Error('Simulation workspace is unavailable');
    }
    for (let attempt = 0; attempt < 20; attempt += 1) {
      const candidate = this.getCurrentDataView();
      if (candidate instanceof SimulationComponent && candidate.simOverview) return candidate;
      await new Promise<void>(resolve => setTimeout(resolve, 25));
    }
    throw new Error('Simulation workspace did not become ready');
  }

  private async applyAgentSimulationConfiguration(tabId: string, modelUuid: string, revision: number, configuration: import('../../interfaces/Simulation').SimulationSendValues, openConfiguration = false): Promise<void> {
    const simulation = await this.simulationForAgent(tabId);
    if (simulation.savedDocument.model_UUID !== modelUuid) throw new Error('Simulation model changed');
    simulation.simOverview.applyValidatedConfiguration(configuration, revision);
    if (openConfiguration && !simulation.simOverview.isOverviewActive) simulation.simOverview.toggle();
  }

  private async runAgentSimulation(tabId: string, revision: number): Promise<void> {
    const simulation = await this.simulationForAgent(tabId);
    if (simulation.simOverview.getSimulationSnapshot().revision !== revision) throw new Error('Simulation configuration changed');
    // A RUN_SIMULATION action is not complete until the simulator has returned
    // its result.  Returning early made the Agent client start its result
    // continuation against a stale/empty snapshot and could surface its generic
    // "could not be applied" error even though the run later succeeded.
    await simulation.simOverview.runCurrentConfiguration();
  }

  private async saveAgentSimulation(tabId: string, mode: 'SAVE' | 'SAVE_AS', name?: string): Promise<void> {
    const simulation = await this.simulationForAgent(tabId);
    await simulation.simOverview.saveCurrentConfigurationForAgent(mode, name);
  }

  private async loadAgentSimulation(tabId: string, simulationUuid: string): Promise<void> {
    await (await this.simulationForAgent(tabId)).simOverview.loadConfigurationForAgent(simulationUuid);
  }

  selectTabType(importType: ImportEnum): TabType{
    switch (importType) {
      case ImportEnum.IMPORT_NEW_TAB:
        return TabType.VIEW
      case ImportEnum.NEW_TAB_CONNECTION:
        return TabType.MODEL_CREATOR
    }
    return TabType.MODEL_CREATOR
  }

  closeOthers(i: number) {
    if (i == null) return;
    const keep = this.tabs[i];
    this.tabs = [keep];
    setTimeout(()=>{
      this.changeTab(0);
    });
  }

  closeRight(i: number) {
    if (i == null) return;
    this.tabs = this.tabs.slice(0, i + 1);
    setTimeout(()=>{
      this.changeTab(Math.min(this.selectedIndex, i));
    });
  }

  closeTab(i: number) {
    if (i == null || i < 0 || i >= this.tabs.length) return;
    const wasSelected = i === this.selectedIndex;
    this.tabs.splice(i, 1);

    if (wasSelected) {
      setTimeout(() => {
        this.changeTab(Math.max(0, i - 1));
      });
    }
  }

  openOnRight(e: MouseEvent, i: number) {
    e.preventDefault();
    e.stopPropagation();
    if(this.tabs.length <= 1) return;
    this.contextIndex = i;
    this.menuTrigger.openMenu();
    const overlay = this.menuTrigger['_overlayRef'];
    if (overlay) {
      // update its position strategy to absolute mouse coords
      overlay.updatePositionStrategy(
        this.overlay
          .position()
          .flexibleConnectedTo({ x: e.clientX, y: e.clientY })
          .withPositions([
            {
              originX: 'start',
              originY: 'bottom',
              overlayX: 'start',
              overlayY: 'bottom'
            }
          ])
      );
    }
  }

  newTabFromStorage() {
    let connection = this.sharedService.default_database_connection;
    if(!connection) return;
    let conn = {
      name_value: "",
      containers: [],
      connection: this.sharedService.default_database_connection,
      model_UUID: "",
      hierarchy: "",
    } as INeo4j;

    let importer = {
      connection: conn,
      type: ImportEnum.NEW_TAB_CONNECTION,
      view: undefined
    } as Importer;

    this.onNewTab(importer);
  }

  onLogout = ()=>{
    this.sharedService.clearTokens();
    this.router.navigate(['/login']);
  }

  isTaxonomyAvailable(){
    let dataView = this.getCurrentDataView(); 
    if(!dataView) return this.sharedService.data_service;
    return this.sharedService.data_service && dataView.IsViewEditable();
  }
}
